import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import FilterBar, { type FilterGroup } from "./components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  categoryLabels,
  categoryTagline,
  genreOptions,
  movies,
  regionOptions,
  yearOptions,
  type MovieCategory,
} from "@/mocks/movies";

const PAGE_SIZE = 8;
const sortOptions = ["最新更新", "最热", "评分"];

const VALID: MovieCategory[] = ["movie", "tv", "variety"];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const category = (VALID.includes(type as MovieCategory) ? type : "movie") as MovieCategory;

  const [region, setRegion] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("最新更新");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setRegion("全部");
    setGenre("全部");
    setYear("全部");
    setSort("最新更新");
    setPage(1);
  }, [category, searchParams]);

  usePageMeta(
    `${categoryLabels[category]}频道 - 91在线视频`,
    `${categoryLabels[category]}频道汇集高清${categoryLabels[category]}资源，支持地区、年份、类型多维筛选与排序，91在线视频为您提供丰富的影视内容与便捷的浏览体验。`,
  );

  const filtered = useMemo(() => {
    const list = movies.filter((m) => m.category === category);
    return list
      .filter((m) => region === "全部" || m.region === region)
      .filter((m) => year === "全部" || String(m.year) === year)
      .filter((m) => genre === "全部" || m.genres.includes(genre));
  }, [category, region, year, genre]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sort === "最热") list.sort((a, b) => b.views - a.views);
    else if (sort === "评分") list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return list;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setRegion("全部");
    setGenre("全部");
    setYear("全部");
    setSort("最新更新");
    setPage(1);
  };

  const groups: FilterGroup[] = [
    {
      key: "region",
      label: "地区",
      options: regionOptions[category],
      value: region,
      onChange: (v) => {
        setRegion(v);
        setPage(1);
      },
    },
    {
      key: "year",
      label: "年份",
      options: yearOptions,
      value: year,
      onChange: (v) => {
        setYear(v);
        setPage(1);
      },
    },
    {
      key: "genre",
      label: "类型",
      options: genreOptions[category],
      value: genre,
      onChange: (v) => {
        setGenre(v);
        setPage(1);
      },
    },
  ];

  const activeCount = [region, year, genre].filter((v) => v !== "全部").length;

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: categoryLabels[category] }]} />

      <div className="mt-4 flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
          {categoryLabels[category]}频道
        </h1>
        <p className="text-sm text-foreground-500">{categoryTagline[category]}</p>
      </div>

      <div className="mt-6">
        <FilterBar groups={groups} onReset={reset} activeCount={activeCount} />
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-600 text-accent-500">{sorted.length}</span> 部作品
        </p>
        <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {sortOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setSort(option);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                sort === option
                  ? "bg-primary-500 font-600 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {pageItems.length === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300/60 py-20 text-foreground-500">
          <i className="ri-emotion-sad-line text-5xl" />
          <p className="text-base font-600 text-foreground-700">未找到相关影视作品</p>
          <p className="text-sm">试试调整筛选条件，或重置后再看看</p>
          <button
            type="button"
            onClick={reset}
            className="mt-2 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-600 text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
          {pageItems.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      )}

      <Pagination current={page} total={totalPages} onChange={setPage} />
    </div>
  );
}